import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { useCallback, useMemo } from 'react'

import { useCurrentUser } from '~/data/api/hooks/users'
import { formatUnitSystemToDistanceUnits } from '~/data/formatters/distance'
import type { EfficiencyUnit, WeightUnit } from '~/data/types'
import type { PreferredDistanceUnit } from '~/data/types/preferredUnits'
import type { TimeZone } from '~/data/types/timezone'
import { timezones } from '~/data/types/timezone'

export type TimeFormat = 'hh:mm' | 'HH:mm'
export type TzDayjs = (date: dayjs.ConfigType) => Dayjs

export function useUserPrefs() {
  const { synopUser } = useCurrentUser()

  const {
    preferredDistanceUnits: maybeDistanceSystem,
    preferredEfficiencyUnits: maybeEfficiencyUnits,
    preferredTemperatureUnits: maybeTemperatureUnits,
    preferredTimeZone
  } = synopUser ?? {}

  const preferredDistanceSystem = maybeDistanceSystem || 'Imperial (Feet / Miles)'
  const preferredUnitSystem = getPreferredUnitSystem(preferredDistanceSystem)

  const preferredDistanceUnits = formatUnitSystemToDistanceUnits(preferredUnitSystem)
  const preferredTemperatureUnits = maybeTemperatureUnits || 'fahrenheit'
  // There is no stored weight preference; it follows the distance system (metric → kg, else lb).
  const preferredWeightUnits: WeightUnit = preferredUnitSystem === 'metric' ? 'kg' : 'lb'
  const preferredEfficiencyUnits =
    (maybeEfficiencyUnits?.toLowerCase() as EfficiencyUnit) || (preferredDistanceUnits === 'mi' ? 'kwh/mi' : 'kwh/km')

  const timezone = useMemo(() => {
    return preferredTimeZone && timezones.includes(preferredTimeZone)
      ? preferredTimeZone
      : (dayjs.tz.guess() as TimeZone)
  }, [preferredTimeZone])

  /** Given a date, returns a dayjs object with the user's preferred timezone set */
  const tzDayjs = useCallback((date?: dayjs.ConfigType) => dayjs(date).tz(timezone), [timezone])

  return {
    preferredTemperatureUnits,
    preferredTimeZone: timezone,
    preferredUnitSystem,
    preferredDistanceUnits,
    preferredEfficiencyUnits,
    preferredWeightUnits,
    tzDayjs
  }
}

export type UnitSystem = 'imperial' | 'uk' | 'metric'
function getPreferredUnitSystem(preferredDistanceUnits: PreferredDistanceUnit): UnitSystem {
  if (preferredDistanceUnits.startsWith('Imperial')) return 'imperial'
  if (preferredDistanceUnits.startsWith('UK')) return 'uk'
  return 'metric'
}
